import { type HTMLAttributes, type KeyboardEvent, type Ref } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { ItemSizeContext } from "./ItemContext";
import { ItemGroup } from "./ItemGroup";
import { ItemMedia } from "./ItemMedia";
import { ItemContent } from "./ItemContent";
import { ItemTitle } from "./ItemTitle";
import { ItemDescription } from "./ItemDescription";
import { ItemActions } from "./ItemActions";
import { ItemHeader } from "./ItemHeader";
import { ItemFooter } from "./ItemFooter";

// A versatile row for displaying media + title/description + actions. Namespaced compound
// (Item.Group / Item.Media / Item.Content / Item.Title / Item.Description / Item.Actions /
// Item.Header / Item.Footer) -- this is exactly the "system of parts the consumer
// arranges/omits/reorders" shape the compound-component pattern is for (optional media,
// optional header/footer/actions, arbitrary ordering).
const itemVariants = cva(
  "group/item flex flex-wrap items-center rounded-md border border-transparent text-sm transition-colors duration-100 outline-none focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border-border",
        muted: "bg-[color-mix(in_srgb,hsl(var(--muted))_50%,transparent)]",
        selectable: "border-border bg-transparent cursor-pointer hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,transparent)]",
      },
      size: {
        default: "gap-4 p-4",
        sm: "gap-2.5 px-4 py-3",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ItemProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof itemVariants> {
  ref?: Ref<HTMLDivElement>;
}

const ItemRoot = ({ className, variant, size, ref, onKeyDown, children, ...props }: ItemProps) => {
  const selectable = variant === "selectable";

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (selectable && (event.key === "Enter" || event.key === " ")) {
      // Selectable rows are activated via their own `onClick` prop, not a nested control (by
      // convention they don't include an `Item.Actions` slot) -- re-dispatch a real click so
      // Enter/Space match native button activation instead of duplicating the click handler.
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  return (
    <div
      ref={ref}
      data-slot="item"
      data-variant={variant ?? "default"}
      data-size={size ?? "default"}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={handleKeyDown}
      className={cn(itemVariants({ variant, size }), className)}
      {...props}
    >
      <ItemSizeContext.Provider value={size ?? "default"}>{children}</ItemSizeContext.Provider>
    </div>
  );
};

export const Item = Object.assign(ItemRoot, {
  Group: ItemGroup,
  Media: ItemMedia,
  Content: ItemContent,
  Title: ItemTitle,
  Description: ItemDescription,
  Actions: ItemActions,
  Header: ItemHeader,
  Footer: ItemFooter,
});

export { itemVariants };
